import axios from "axios";

import { getToken, logout } from "../auth";

// Axios instance pointed at the standalone Flask backend. Base URL mirrors the
// vasana-web pattern of reading REACT_APP_API_URL from the environment.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5001",
  // The deep-dive endpoint is heavy (~cold seconds until the cache warms), which
  // exceeded the old 30s timeout and aborted the request every time.
  timeout: 120000,
});

// React Strict Mode intentionally re-runs mount effects in development. Several
// parts of the dashboard also need the same data at startup (notably `/api/me`
// for the route guard and the dashboard). Keep only *in-flight* GET requests so
// concurrent callers share one HTTP request without turning this into a cache:
// every later refresh still reaches the API as it did before.
const inFlightGets = new Map();

export function getOnce(url, config = {}) {
  const key = `${url}:${JSON.stringify(config.params || {})}`;
  const existing = inFlightGets.get(key);
  if (existing) return existing;

  const request = api.get(url, config);
  inFlightGets.set(key, request);

  const removeRequest = () => {
    // Do not remove a newer request for the same key.
    if (inFlightGets.get(key) === request) inFlightGets.delete(key);
  };
  // Supply both handlers so a failed request is still removed without creating
  // an unhandled rejected promise from `finally`.
  request.then(removeRequest, removeRequest);

  return request;
}

// Attach the stored access token to every request (the API verifies it and
// enforces platform_admin). Sent as a bare token to match vasana-be.
api.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) cfg.headers.Authorization = token;
  return cfg;
});

// On 401 (missing/expired/invalid platform token), send the visitor back to the
// platform login. logout() clears the shared cookie and redirects.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      logout();
    }
    return Promise.reject(err);
  }
);

export default api;
